import { Checkbox, Label } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import type { NotificationDestination } from "@/hooks/api/notifications"

import { DestinationStatusBadge } from "./DestinationStatusBadge"

export interface DestinationSelectorGroupLabels {
  selected: string
  available: string
  selectedEmpty: string
}

export interface DestinationSelectorProps {
  destinations: NotificationDestination[]
  selectedIds: string[]
  disabled?: boolean
  emptyLabel: string
  readyLabel: string
  pendingLabel: string
  disabledLabel: string
  verificationPendingLabel?: string
  verificationExpiredLabel?: string
  groupLabels?: DestinationSelectorGroupLabels
  getUsageLabel?: (destination: NotificationDestination) => string
  onChange: (destinationIds: string[]) => void
}

export const DestinationSelector = ({
  destinations,
  selectedIds,
  disabled = false,
  emptyLabel,
  readyLabel,
  pendingLabel,
  disabledLabel,
  verificationPendingLabel,
  verificationExpiredLabel,
  groupLabels,
  getUsageLabel,
  onChange,
}: DestinationSelectorProps) => {
  if (destinations.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>
  }

  const selected = new Set(selectedIds)
  const renderDestination = (destination: NotificationDestination) => {
    const checkboxId = `notification-destination-${destination.id}`
    const usageLabel = getUsageLabel?.(destination)
    const isSelected = selected.has(destination.id)

    return (
      <div key={destination.id} className="flex items-start gap-3 px-3 py-3">
        <Checkbox
          id={checkboxId}
          className="mt-0.5"
          checked={isSelected}
          disabled={disabled}
          onCheckedChange={(checked) => {
            const next = new Set(selected)
            if (checked) next.add(destination.id)
            else next.delete(destination.id)
            onChange(Array.from(next))
          }}
        />
        <Label
          htmlFor={checkboxId}
          className={cn(
            "min-w-0 flex-1 cursor-pointer",
            groupLabels && "flex-col items-start gap-0.5"
          )}
        >
          <span
            className={cn(
              "block truncate text-sm font-medium",
              groupLabels && "w-full"
            )}
          >
            {destination.name}
          </span>
          <span
            className={cn(
              "block truncate text-xs text-muted-foreground",
              groupLabels && "w-full"
            )}
          >
            {destination.maskedEndpoint}
          </span>
          {usageLabel ? (
            <span
              className="block break-words text-xs text-muted-foreground"
              title={usageLabel}
            >
              {usageLabel}
            </span>
          ) : null}
        </Label>
        <div className="flex shrink-0 items-center gap-1">
          {!destination.isReady ? (
            <DestinationStatusBadge
              destination={destination}
              readyLabel={readyLabel}
              pendingLabel={pendingLabel}
              disabledLabel={disabledLabel}
              verificationPendingLabel={verificationPendingLabel}
              verificationExpiredLabel={verificationExpiredLabel}
            />
          ) : null}
        </div>
      </div>
    )
  }

  if (!groupLabels) {
    return (
      <div className="min-w-0 divide-y overflow-hidden rounded-md border">
        {destinations.map(renderDestination)}
      </div>
    )
  }

  const selectedDestinations = destinations.filter((destination) =>
    selected.has(destination.id)
  )
  const availableDestinations = destinations.filter(
    (destination) => !selected.has(destination.id)
  )

  return (
    <div className="space-y-4">
      <section className="space-y-2" aria-label={groupLabels.selected}>
        <h4 className="text-xs font-medium text-muted-foreground">
          {groupLabels.selected}
        </h4>
        {selectedDestinations.length ? (
          <div className="min-w-0 divide-y overflow-hidden rounded-md border">
            {selectedDestinations.map(renderDestination)}
          </div>
        ) : (
          <p className="rounded-md border border-dashed px-3 py-3 text-sm text-muted-foreground">
            {groupLabels.selectedEmpty}
          </p>
        )}
      </section>
      {availableDestinations.length ? (
        <section className="space-y-2" aria-label={groupLabels.available}>
          <h4 className="text-xs font-medium text-muted-foreground">
            {groupLabels.available}
          </h4>
          <div className="min-w-0 divide-y overflow-hidden rounded-md border">
            {availableDestinations.map(renderDestination)}
          </div>
        </section>
      ) : null}
    </div>
  )
}
